import { Image, type ImageProps } from '@mantine/core'
import {
  type ComponentPropsWithoutRef,
  type ReactEventHandler,
  useState
} from 'react'

import { imageUrl, type ImageOpts } from 'src/utils'

type Props = Omit<ImageProps, 'src' | 'onError'> &
  // Mantine's `ImageProps` alias omits the native <img> attributes (alt, etc.)
  // that the polymorphic component still accepts — pull them back in.
  Omit<
    ComponentPropsWithoutRef<'img'>,
    'src' | 'onError' | keyof ImageProps
  > & {
    // Raw source URL — the imgproxy transform is applied internally.
    src: string | null | undefined
    // imgproxy resize / quality options.
    transform?: ImageOpts
    onError?: ReactEventHandler<HTMLImageElement>
  }

export const ProxiedImage = ({ src, transform, onError, ...rest }: Props) => {
  const base = imageUrl(src, transform)

  // Retry the proxied transform once on a transient error (e.g. imgproxy 504/503), then fall
  // back to the raw src when imgproxy can't reach the source host (e.g. minio.skalx.ai).
  // Reset all state on src change via render-time derived state (React's recommended pattern).
  const [prevBase, setPrevBase] = useState(base)
  const [retried, setRetried] = useState(false)
  const [useDirect, setUseDirect] = useState(false)

  if (base !== prevBase) {
    setPrevBase(base)
    setRetried(false)
    setUseDirect(false)
  }

  const onImageError: ReactEventHandler<HTMLImageElement> = (event) => {
    if (base && !retried) {
      setRetried(true)

      return
    }
    if (base && src && !useDirect) {
      setUseDirect(true)

      return
    }
    onError?.(event)
  }

  const getCurrentSrc = () => {
    if (useDirect) return src
    if (retried) return `${base}?r=1`

    return base
  }

  return <Image src={getCurrentSrc()} onError={onImageError} {...rest} />
}
